/**
 * Greek → Latin transliteration for filenames. The `[^a-z0-9]+` filter below
 * keeps only ASCII, so without this every Greek phrase would slug to the empty
 * string and each clip would overwrite the same file. Filenames are internal,
 * so any consistent scheme works. Applied AFTER accent-folding + lower-casing
 * (so ά→α→"a") and BEFORE the ASCII collapse. ASCII input is untouched, so no
 * existing English/French clip filename moves.
 *
 * NOTE: the map is lossy (η ι υ → i/y; ο ω → o), so two distinct Greek phrases
 * could collide on one filename. The batch clip generator + gen-manifest.mjs
 * assert slug uniqueness to catch that; keep those asserts.
 */
const GREEK_TO_LATIN: Record<string, string> = {
  α: "a", β: "v", γ: "g", δ: "d", ε: "e", ζ: "z", η: "i", θ: "th", ι: "i", κ: "k",
  λ: "l", μ: "m", ν: "n", ξ: "x", ο: "o", π: "p", ρ: "r", σ: "s", ς: "s", τ: "t",
  υ: "y", φ: "f", χ: "ch", ψ: "ps", ω: "o",
};

/**
 * Spoken phrase → clip filename slug, e.g. "Now draw two big eyes" →
 * "now-draw-two-big-eyes".
 *
 * Accents are folded away FIRST (é→e, ê→e, ç→c). Without that, `[^a-z0-9]+`
 * would turn every accented letter into a separator — "grosse tête" would slug
 * to "grosse-t-te", and two phrases differing only by an accent would collide.
 * Folding is a no-op for pure-ASCII English phrases, so every existing clip
 * filename is unchanged.
 */
export function clipSlug(phrase: string): string {
  return phrase
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .split("")
    .map((c) => GREEK_TO_LATIN[c] ?? c)
    .join("")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
